import React, { useState, useEffect, useRef } from 'react';
import Camera from './Camera';
import FileUploader from './FileUploader';
import SecuredImage from './SecuredImage';
import AttachmentDownloadButton from './AttachmentDownloadButton';
import { Dialog } from '@headlessui/react';
import SearchSelect from '../pages/SearchSelect';
import axios from 'axios';
import config from '../config';
import { getToken } from '../auth';
import { DatePicker } from './Planning/components/DatePicker';
import PlannedInput from './PlannedInput';
import { parseYMDToLocalDate, formatDateToYMD, formatDisplayDate, TIMING_YEAR_START, TIMING_YEAR_END } from '../utils/dateFormat';
import { expectedText, plannedDateOf, timeIntervalParts } from '../utils/planning';
import { hasNoDate } from '../utils/taskStatus';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useVanoCodes } from '../context/VanoCodesContext';
import { ACTIVITY_CATEGORIES } from '../utils/vanoCodes';
import { isArchived, optionLabel } from '../utils/archive';
import { DIARY_MAX_LENGTH } from '../utils/trialFieldLimits';
import { apiErrorMessage } from '../utils/apiError';
import { closingTaskName } from '../utils/trialClose';
import { getUserInfo } from '../user';
import CompletedBySelect from './CompletedBySelect';
import { taskKey } from '../utils/excelReport/allDataColumns';
import CharCount from './CharCount';

// A date field built on the shared popover calendar (`DatePicker.jsx`), with a "Clear" action
// since Done Date is optional and the calendar itself has no way to unset a selection once
// made. `startMonth`/`endMonth` are optional passthroughs to `DatePicker` - left unset here
// (Done Date keeps the picker's default range), only the Time Interval "Exact date" call site
// below passes them.
function DateField({ value, onChange, startMonth, endMonth, label }) {
  return (
    <div className="flex items-center gap-2">
      <DatePicker
        selected={parseYMDToLocalDate(value)}
        onChange={(date) => onChange(formatDateToYMD(date))}
        startMonth={startMonth}
        endMonth={endMonth}
        label={label}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
        >
          Clear
        </button>
      )}
    </div>
  );
}

// Map va_id to type string for dropdown
const vaIdToType = (va_id) => {
  if (String(va_id) === '1') return 'Visits';
  if (String(va_id) === '2') return 'Applications';
  if (String(va_id) === '8') return 'Activities';
  return '';
};
// Allowed va_id values for selection
const ALLOWED_VA_IDS = ['1', '2', '8'];
// Derive VA No ("vano") from va_id
const vaIdToVano = (va_id) => {
  const v = String(va_id || '');
  return ALLOWED_VA_IDS.includes(v) ? v : '';
};

// The customer's expected date is a future date: last year to 3 years ahead, as the timing choices
const EXPECTED_START_MONTH = new Date(TIMING_YEAR_START, 0, 1);
const EXPECTED_END_MONTH = new Date(TIMING_YEAR_END, 11, 31);

// Done Date is historical (oldest trials go back to 2010), unlike the forward-looking dates
const DONE_DATE_START_MONTH = new Date(2010, 0, 1);
const DONE_DATE_END_MONTH = new Date(new Date().getFullYear() + 1, 11, 31);

// A section of the form: a heading and its fields, in a grid that is one column on a tablet, two from lg:
function Section({ title, children, testId }) {
  return (
    <section className="rounded-lg border border-gray-200 p-4 dark:border-gray-700" data-testid={testId}>
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h3>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{children}</div>
    </section>
  );
}

const parsePersonInitials = (value) => String(value || '')
  .split(/[,/\s]+/)
  .map(initials => initials.trim())
  .filter(Boolean);

const personOption = (initials, users) => {
  const user = users.find(candidate => String(candidate.initials || '').toLowerCase() === initials.toLowerCase());
  return user || {
    id: `ghost-${initials}`,
    initials,
    name: initials,
    isGhost: true,
  };
};

// A user as "Name (INI)", or "…" until the users have loaded
const userName = (id, users) => {
  const user = users.find(candidate => Number(candidate.id) === Number(id));
  return user ? `${user.name} (${user.initials})` : '…';
};

// Who completed a done task: the user in completed_by, else (older tasks) the persons as written
const completedByName = (entry, users) => (entry.completed_by != null ? userName(entry.completed_by, users) : entry.persons || '—');

export default function DiaryEntryModal({ open, onClose, onSave, initialData, onAttachmentUpdate, trialDiaries = [] }) {
  const vanoCodes = useVanoCodes();
  // A new entry is numbered and named by the API ("Visit 09", "Application C", "Pegs Up"); one
  // created that way keeps its type and number (fixed_name); any task's name can be changed with "Change…".
  const isNew = !initialData?.id;
  // A task without a Time interval (none, "?" or "TBD", e.g. a visit Edit Trial added) gets its first one here, as a
  // new task does, without a reason (customer point 71); one it has is changed with "Change…" and a reason
  const intervalOpen = isNew || (hasNoDate(initialData?.timeinterval) && hasNoDate(initialData?.timeinterval_text));
  // Debug: log initialData once when modal opens
  useEffect(() => { if (open) console.log('[DiaryEntryModal] initialData:', initialData); }, [open, initialData]);
  // Use V_A or va_id for type selector
  const getTypeFromInitial = (data) => {
    if (data?.V_A !== undefined) return vaIdToType(data.V_A);
    if (data?.va_id !== undefined) return vaIdToType(data.va_id);
    return '';
  };

  // Helper to format date to yyyy-MM-dd
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Accepts ISO, yyyy-MM-dd, or yyyy-MM-ddTHH:mm:ssZ
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString.split(' ')[0];
    return d.toISOString().slice(0, 10);
  };

  const [form, setForm] = useState({
    trial_id: initialData?.trial_id || '',
    va_id: initialData?.va_id || initialData?.V_A || '',
    type: getTypeFromInitial(initialData),
    task: initialData?.task || '',
    description: initialData?.description || '',
    field: '',
    donedate: formatDateForInput(initialData?.donedate) || '',
    dataonpsion: initialData?.dataonpsion !== undefined ? !!initialData.dataonpsion : true,
    dataonpaper: !!initialData?.dataonpaper,
    persons: initialData?.persons || '',
    bbch: initialData?.bbch || '',
    timeinterval: initialData?.timeinterval || '',
    timeinterval_text: initialData?.timeinterval_text || '',
    // The Planned value as chosen (a Planned date set before planned_interval existed counts)
    planned_interval: initialData?.planned_interval || initialData?.planned_date || '',
    // VA No is derived from type (va_id) and not user-editable
    vano: initialData?.vano || vaIdToVano(initialData?.va_id ?? initialData?.V_A) || '',
    delflag: !!initialData?.delflag,
  });

  // A change of the customer's Time interval of an existing task (by any user, customer point 26): its date and/or
  // text and one reason, saved on its own (and logged by the API, a row per changed part). null when not open.
  const [intervalChange, setIntervalChange] = useState(null);

  // What "Change the time interval" sends: the reason and each part that changed. An old text kept in
  // timeinterval is written as the date and text it now has, so it is not lost when only one part changes.
  function intervalChangeBody() {
    const before = timeIntervalParts(form);
    const body = { timeinterval_reason: intervalChange.reason };
    const text = intervalChange.text.trim();
    if (intervalChange.date === before.date && text === before.text) return body;
    const oldText = !before.date && String(form.timeinterval || '').trim() !== '';
    if (oldText || intervalChange.date !== before.date) body.timeinterval = intervalChange.date || null;
    if (oldText || text !== before.text) body.timeinterval_text = text || null;
    return body;
  }

  async function saveIntervalChange() {
    const token = getToken();
    try {
      await axios.patch(
        `${config.apiBaseUrl}/trials/${form.trial_id}/diaries/${initialData.id}`,
        intervalChangeBody(),
        { headers: { Authorization: token ? `Bearer ${token}` : undefined } }
      );
      setForm(f => ({ ...f, timeinterval: intervalChange.date, timeinterval_text: intervalChange.text }));
      setIntervalChange(null);
      if (onSave) onSave();
    } catch (err) {
      setIntervalChange(c => ({ ...c, error: apiErrorMessage(err, 'Failed to change the Time interval.') }));
    }
  }

  // Debug: Dump form state on every render AFTER form is defined
  useEffect(() => {
    if (open) console.log('[DiaryEntryModal] form state:', JSON.stringify(form, null, 2));
  }, [form, open]);

  // NOTE: vano is intentionally NOT re-derived from va_id here. That used to run on every
  // mount (including when editing an existing entry) and would silently overwrite a real
  // canonical vano code (e.g. "A34" for a system-generated "Pegs up" entry) with the raw
  // type id ("8"), corrupting it on save. The Type <select>'s own onChange already sets
  // va_id/type/vano together whenever the user actively changes the type - that's the only
  // place vano should be re-derived.

  // Why the last save did not go through ({ message, retry }), shown above the buttons instead of alert()
  const [saveError, setSaveError] = useState(null);
  // Who did it: the task's, else (not done yet) the signed-in user; "" for an old done task without one
  const [completedBy, setCompletedBy] = useState('');
  useEffect(() => {
    if (initialData?.completed_by != null) {
      setCompletedBy(Number(initialData.completed_by));
      return undefined;
    }
    if (initialData?.donedate) {
      setCompletedBy('');
      return undefined;
    }
    let alive = true;
    getUserInfo().then(me => { if (alive) setCompletedBy(me?.id ?? ''); });
    return () => { alive = false; };
  }, [initialData]);
  // Removing a task's done date is confirmed above the buttons before it is saved
  const [confirmRemoveDone, setConfirmRemoveDone] = useState(false);
  const removeDoneConfirmed = useRef(false);
  // The name: locked until "Change…"; a new one is confirmed above the buttons before it is saved
  const [renaming, setRenaming] = useState(false);
  const [confirmRename, setConfirmRename] = useState(false);
  const renameConfirmed = useRef(false);
  useEffect(() => {
    setRenaming(false);
    setConfirmRename(false);
  }, [initialData]);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showFileUploaderModal, setShowFileUploaderModal] = useState(false);
  const [diaryAttachments, setDiaryAttachments] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const personsTouched = useRef(false);
  const personsFor = useRef(undefined);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const token = getToken();
    axios.post(`${config.apiBaseUrl}/masterdata/users`, {}, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    }).then(response => {
      setUsers(Array.isArray(response.data) ? response.data : []);
    }).catch(() => setUsers([]));
  }, [open]);

  useEffect(() => {
    // The entry's persons, matched to the users once they have loaded - unless the user has already
    // changed the selection (the users can load after that, and must not undo it)
    // (another entry: its own persons again)
    if (personsFor.current !== initialData) {
      personsFor.current = initialData;
      personsTouched.current = false;
    }
    if (personsTouched.current) return;
    const initials = parsePersonInitials(initialData?.persons);
    setSelectedPersons(initials.map(value => personOption(value, users)));
  }, [initialData, users]);

  // Update form when initialData changes (for editing existing entries)
  useEffect(() => {
    if (initialData) {
      setForm(f => ({
        ...f,
        trial_id: initialData?.trial_id || '',
        va_id: initialData?.va_id || initialData?.V_A || '',
        type: getTypeFromInitial(initialData) || (initialData?.va_id ? vaIdToType(initialData.va_id) : ''),
        task: initialData?.task || '',
        description: initialData?.description || '',
        field: '',
        donedate: formatDateForInput(initialData?.donedate) || '',
        dataonpsion: initialData?.dataonpsion !== undefined ? !!initialData.dataonpsion : true,
        dataonpaper: !!initialData?.dataonpaper,
        persons: initialData?.persons || '',
        bbch: initialData?.bbch || '',
        timeinterval: initialData?.timeinterval || '',
        timeinterval_text: initialData?.timeinterval_text || '',
        planned_interval: initialData?.planned_interval || initialData?.planned_date || '',
        // Ensure vano mirrors va_id when editing or prefilled
        vano: initialData?.vano || vaIdToVano(initialData?.va_id ?? initialData?.V_A) || '',
        delflag: !!initialData?.delflag,
      }));
      setDiaryAttachments(initialData?.attachments || []);
    }
    setSaveError(null);
  }, [initialData]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  function handleChange(e) {
    const { name, value, type: inputType, checked } = e.target;
    setForm(f => ({
      ...f,
      [name]: inputType === 'checkbox' ? checked : value
    }));
  }

  // The payload's fields that differ from the entry as it was opened. Persons only when the user
  // changed them (their ids are not on the entry as loaded).
  function changedFields(payload) {
    const before = {
      task: initialData?.task || '',
      vano: initialData?.vano || vaIdToVano(initialData?.va_id ?? initialData?.V_A) || '',
      description: initialData?.description || null,
      donedate: formatDateForInput(initialData?.donedate) || null,
      dataonpsion: initialData?.dataonpsion !== undefined ? !!initialData.dataonpsion : true,
      dataonpaper: !!initialData?.dataonpaper,
      bbch: initialData?.bbch || '',
      planned_interval: initialData?.planned_interval || initialData?.planned_date || null,
      delflag: !!initialData?.delflag,
      // (sent only for a task without a Time interval, which gets its first one here)
      timeinterval: initialData?.timeinterval || null,
      timeinterval_text: initialData?.timeinterval_text || null,
    };
    const plannedChanged = before.planned_interval !== payload.planned_interval;
    return Object.fromEntries(Object.entries(payload).filter(([key, value]) => {
      if (key === 'trial_id') return false;
      if (key === 'persons' || key === 'person_ids') return personsTouched.current;
      // The Planned date goes with the Planned value: both or neither
      if (key === 'planned_date') return plannedChanged;
      return !(key in before) || before[key] !== value;
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // A done date removed is confirmed first: the task becomes open again (who completed it is cleared by the API)
    if (initialData?.id && initialData?.donedate && !form.donedate && !removeDoneConfirmed.current) {
      setConfirmRemoveDone(true);
      return;
    }
    // A new name: never empty, and confirmed first (only the name changes, the VA No stays)
    if (!isNew && form.task.trim() !== (initialData?.task || '').trim()) {
      if (!form.task.trim()) {
        setSaveError({ message: 'Give the task a name.', retry: false });
        return;
      }
      if (!renameConfirmed.current) {
        setConfirmRename(true);
        return;
      }
    }
    removeDoneConfirmed.current = false;
    renameConfirmed.current = false;
    setConfirmRemoveDone(false);
    setConfirmRename(false);
    // A new entry needs a valid type; an existing one keeps its own (it is not sent)
    setSaveError(null);
    if (isNew && (!form.va_id || !ALLOWED_VA_IDS.includes(String(form.va_id)))) {
      setSaveError({ message: 'Please select a Type before saving (previous value is not permitted).', retry: false });
      return;
    }
    // Use va_id directly (numeric expected by backend)
    let va_id = form.va_id;
    const va_idParsed = va_id ? parseInt(va_id, 10) : null;
    const token = getToken();
    // Format dates for backend
    const formatDate = (dateString) => {
      if (!dateString) return null;
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return dateString.split(' ')[0];
      return d.toISOString().slice(0, 10);
    };
    // A person is optional: an entry can be planned before anyone is assigned to it
    if (isNew && String(form.va_id) === '8' && !form.field) {
      setSaveError({ message: 'Please choose which activity to add.', retry: false });
      return;
    }
    const payload = {
      trial_id: form.trial_id,
      // A new entry's type, number and name are given here or by the API; for an activity, which one
      ...(isNew ? { va_id: va_idParsed, ...(String(form.va_id) === '8' ? { field: form.field } : {}) } : { task: form.task.trim(), vano: form.vano || '' }),
      description: form.description || null,
      donedate: formatDate(form.donedate),
      dataonpsion: !!form.dataonpsion,
      dataonpaper: !!form.dataonpaper,
      persons: selectedPersons.map(person => person.initials).join(', '),
      person_ids: selectedPersons.filter(person => !person.isGhost).map(person => person.id),
      bbch: form.bbch || '',
      // Who did it, when there is a done date and it is not who the task already has (the API defaults to the
      // signed-in user and records them as who registered it)
      ...(form.donedate && completedBy !== '' && Number(completedBy) !== Number(initialData?.completed_by ?? NaN) ? { completed_by: Number(completedBy) } : {}),
      // The customer's expected date only for a new task or a first one: after that it is locked ("Change…" with a reason)
      // (free text as typed, trimmed here)
      ...(intervalOpen ? { timeinterval: form.timeinterval || null, timeinterval_text: form.timeinterval_text?.trim() || null } : {}),
      planned_interval: form.planned_interval?.trim() || null,
      planned_date: plannedDateOf(form.planned_interval?.trim()),
      delflag: !!form.delflag,
    };
    console.log('[DiaryEntryModal] Submitting payload:', payload);
    try {
      if (initialData?.id) {
        // Update diary entry (PATCH) - only the fields the user changed, so an old entry whose other
        // fields are longer than today's limits can still be saved (as Edit Trial does)
        await axios.patch(
          `${config.apiBaseUrl}/trials/${form.trial_id}/diaries/${initialData.id}`,
          changedFields(payload),
          {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          }
        );
      } else {
        // Create diary entry (POST)
        await axios.post(
          `${config.apiBaseUrl}/trials/${form.trial_id}/diaries`,
          payload,
          {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          }
        );
      }
      // Final Report or Trial Done just completed: the trial page asks whether to close the trial
      const closingTask = initialData?.id && !initialData.donedate && payload.donedate && closingTaskName(initialData) ? initialData : null;
      if (onSave) onSave(payload, { closingTask });
      onClose();
    } catch (err) {
      console.error('[DiaryEntryModal] Save failed:', err?.response?.status, err?.response?.data ?? err);
      // The API's message only (a validation message, or a general one for a server error) - never the raw response
      setSaveError({ message: apiErrorMessage(err, 'Failed to save diary entry.'), retry: true });
    }
  }

  // Reopens a cancelled task without touching donedate or requiring a reason - just clears
  // the cancellation. Reuses handleSubmit's same onSave/onClose success handling; errors use
  // toast (sonner) instead of alert, matching CancelTaskDialog's convention.
  async function handleReactivate() {
    const token = getToken();
    try {
      await axios.patch(
        `${config.apiBaseUrl}/trials/${initialData.trial_id}/diaries/${initialData.id}`,
        { cancelled: false },
        { headers: { Authorization: token ? `Bearer ${token}` : undefined } }
      );
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error('[DiaryEntryModal] Reactivate failed:', err);
      toast.error('Failed to remove cancellation. Please try again.');
    }
  }

  // Handle camera button click
  const handleCameraButtonClick = () => {
    console.log('Camera button clicked');
    setShowCameraModal(true);
  };

  // Handle file uploader button click
  const handleFileUploaderButtonClick = () => {
    console.log('File uploader button clicked');
    setShowFileUploaderModal(true);
  };

  // Handle successful photo/video capture
  const handleCaptureSuccess = async (result) => {
    console.log('Media captured for diary entry:', result);

    // Close the camera modal
    setShowCameraModal(false);

    // If we have attachment info from the upload result, add it directly to the list
    if (result?.attachment && initialData?.id) {
      setDiaryAttachments(prevAttachments => [...prevAttachments, result.attachment]);
    }

    // Also try to refresh from server if callback provided
    if (onAttachmentUpdate && initialData?.id) {
      try {
        const updatedAttachments = await onAttachmentUpdate(initialData.id);
        if (Array.isArray(updatedAttachments)) {
          setDiaryAttachments(updatedAttachments);
        }
      } catch (error) {
        console.log('Server refresh failed, using local attachment data instead');
        // Don't show error - we already added the attachment locally above
      }
    }
  };

  // Handle successful file upload
  const handleFileUploadSuccess = async (results) => {
    console.log('Files uploaded for diary entry:', results);

    // Close the file uploader modal
    setShowFileUploaderModal(false);

    // Refresh attachments from server if callback provided
    if (onAttachmentUpdate && initialData?.id) {
      try {
        const updatedAttachments = await onAttachmentUpdate(initialData.id);
        if (Array.isArray(updatedAttachments)) {
          setDiaryAttachments(updatedAttachments);
        }
      } catch (error) {
        console.log('Server refresh failed after file upload');
      }
    }
  };

  function handleOverlayClick(e) {
    // Only allow overlay click to close when editing an existing diary entry
    // For new diary entries (no initialData or initialData.id), ignore overlay clicks
    if (modalRef.current && e.target === modalRef.current) {
      if (initialData && initialData.id) {
        onClose();
      } else {
        // Ignore click-away when creating a new diary entry
        e.stopPropagation();
      }
    }
  }

  if (!open) return null;
  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-all p-4"
      onClick={handleOverlayClick}
      tabIndex={-1}
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto outline-none" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-emerald-700 dark:text-emerald-400">
              {initialData?.id ? 'Edit Diary Entry' : 'Add Diary Entry'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* !! - cancelled is 0/1 from the API, and React would render a 0 */}
        {!!initialData?.cancelled && (
          <div className="mx-6 mt-4 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 p-3 text-sm text-amber-900 dark:text-amber-200">
            <p className="font-semibold">This task is cancelled</p>
            <p>Reason: {initialData.cancelled_reason || '—'}</p>
            {(() => {
              const canceller = users.find(u => u.id === initialData.cancelled_by);
              return canceller || initialData.cancelled_at ? (
                <p className="text-xs opacity-80">
                  {canceller && `By ${canceller.name || canceller.initials}`}
                  {canceller && initialData.cancelled_at && ' · '}
                  {initialData.cancelled_at && formatDisplayDate(initialData.cancelled_at)}
                </p>
              ) : null;
            })()}
            <p className="mt-1 text-xs opacity-80">
              Setting a Done Date below will reopen this task and remove the cancellation.
            </p>
            <button
              type="button"
              onClick={handleReactivate}
              className="mt-2 rounded border border-amber-600 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
            >
              Remove cancellation
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          <form id="diary-form" onSubmit={handleSubmit} className="space-y-6">
            <input type="hidden" name="trial_id" value={form.trial_id} />
            {/* Do not auto-map va_id in hidden input; preserve original when editing */}

            {/* The task, its dates, who did it, the files - sections that stack on a tablet */}
            <Section title="Task" testId="section-task">
              {/* Type: chosen when the entry is added (va_id as value), fixed after */}
              {isNew ? (
              <div>
                <label className="flex text-sm font-medium mb-1 items-center gap-2">
                  Type
                </label>
                <select
                  name="va_id"
                  value={form.va_id ? String(form.va_id) : ''}
                  onChange={e => {
                    const newVa = e.target.value;
                    setForm(f => ({ ...f, va_id: newVa, type: vaIdToType(newVa), field: '' }));
                  }}
                  className="min-h-10 w-full rounded border px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-emerald-400 focus:border-emerald-500 text-sm"
                  required
                >
                  <option value="" className="text-gray-400 dark:text-gray-500">Select type...</option>
                  <option value="1">Visits</option>
                  <option value="2">Applications</option>
                  <option value="8">Activities</option>
                </select>
              </div>
              ) : (
              <div>
                <span className="block text-sm font-medium mb-1">Type</span>
                <p className="flex min-h-10 items-center gap-2 rounded border border-dashed px-3 text-sm text-gray-600 dark:text-gray-300" data-testid="diary-type">
                  <Lock className="h-4 w-4 text-gray-500" aria-hidden="true" />
                  {form.type || '—'}
                </p>
              </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">VA No</label>
                <input
                  name="vano"
                  value={isNew ? 'Assigned automatically' : form.vano}
                  readOnly
                  aria-readonly="true"
                  className="min-h-10 w-full rounded border px-3 py-2 bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                  title="The number is set automatically: the next visit or application of the trial"
                />
              </div>
              <div className="lg:col-span-2">
                {isNew && String(form.va_id) === '8' ? (
                  <>
                    <label htmlFor="diary-activity" className="block text-sm font-medium mb-1">Activity</label>
                    <select
                      id="diary-activity"
                      value={form.field}
                      onChange={e => setForm(f => ({ ...f, field: e.target.value }))}
                      className="w-full rounded border px-3 py-2 bg-white dark:bg-gray-800 text-sm"
                      required
                    >
                      <option value="">Select activity...</option>
                      {ACTIVITY_CATEGORIES.map(category => {
                        const options = vanoCodes.filter(code => code.visible && code.category === category.key);
                        if (options.length === 0) return null;
                        return (
                          <optgroup key={category.title} label={category.title}>
                            {options.map(code => {
                              const onTrial = trialDiaries.some(d => d.vano === code.vano);
                              return (
                                <option key={code.field} value={code.field} disabled={onTrial}>
                                  {code.label}{onTrial ? ' (already on the trial)' : ''}
                                </option>
                              );
                            })}
                          </optgroup>
                        );
                      })}
                    </select>
                  </>
                ) : (
                  <>
                    <label htmlFor="diary-task" className="block text-sm font-medium mb-1">Task</label>
                    {isNew ? (
                      <p id="diary-task" className="rounded border border-dashed px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {form.va_id ? `Named automatically (${String(form.va_id) === '1' ? 'next visit, e.g. "Visit 09"' : 'next application, e.g. "Application C"'})` : 'Choose a type first'}
                      </p>
                    ) : (
                      // Locked; "Change…" unlocks the name only (its type and VA No stay), confirmed before saving
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          id="diary-task"
                          name="task"
                          value={form.task}
                          onChange={handleChange}
                          readOnly={!renaming}
                          maxLength={DIARY_MAX_LENGTH.task}
                          title={renaming ? undefined : 'Use "Change…" to rename the task'}
                          className="min-w-0 flex-1 rounded border px-3 py-2 read-only:bg-gray-100 read-only:text-gray-600 dark:read-only:bg-gray-800"
                          required
                        />
                        {renaming ? (
                          <button
                            type="button"
                            onClick={() => {
                              setRenaming(false);
                              setForm(f => ({ ...f, task: initialData?.task || '' }));
                            }}
                            className="min-h-10 rounded border px-3 text-sm"
                          >
                            Keep the name
                          </button>
                        ) : (
                          <button
                            type="button"
                            aria-label="Change the task name"
                            onClick={() => setRenaming(true)}
                            className="min-h-10 rounded border px-3 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            Change…
                          </button>
                        )}
                      </div>
                    )}
                    {renaming && <CharCount value={form.task} max={DIARY_MAX_LENGTH.task} />}
                  </>
                )}
              </div>
              <div className="lg:col-span-2">
                <label htmlFor="diary-description" className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  id="diary-description"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  maxLength={DIARY_MAX_LENGTH.description}
                  rows={2}
                  placeholder="What is to be done (Agrolab's notes)"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
                <CharCount value={form.description} max={DIARY_MAX_LENGTH.description} />
              </div>
            </Section>

            <Section title="Dates" testId="section-dates">
              <div className="lg:col-span-2" data-testid="diary-expected">
                <span className="block text-sm font-medium mb-1">Time interval</span>
                {intervalOpen ? (
                  <>
                    <PlannedInput
                      kind="timeinterval"
                      idPrefix="diary-interval"
                      value={timeIntervalParts(form)}
                      onChange={({ date, text }) => setForm(f => ({ ...f, timeinterval: date, timeinterval_text: text }))}
                      startMonth={EXPECTED_START_MONTH}
                      endMonth={EXPECTED_END_MONTH}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {isNew ? 'Set from the customer\'s agreement. It is locked once the task is created.' : 'This task has no Time interval yet: set it from the customer\'s agreement. It is locked once saved.'}
                    </p>
                  </>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex min-h-10 items-center gap-2 rounded border border-dashed px-3 text-sm" data-testid="diary-expected-value">
                      <Lock className="h-4 w-4 text-gray-500" aria-hidden="true" />
                      {expectedText(form) || '—'}
                    </span>
                    {!intervalChange && (
                      <button
                        type="button"
                        onClick={() => setIntervalChange({ ...timeIntervalParts(form), reason: '', error: null })}
                        className="min-h-10 rounded border px-3 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        Change…
                      </button>
                    )}
                    <p className="w-full text-xs text-gray-500 dark:text-gray-400">
                      Set from the customer's agreement when the task was created. It can be changed with a reason.
                    </p>
                  </div>
                )}
                {intervalChange && (
                  <div role="group" aria-label="Change the Time interval" className="mt-3 grid gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/30">
                    <PlannedInput
                      kind="timeinterval"
                      idPrefix="interval-change"
                      dateLabel="New time interval"
                      value={{ date: intervalChange.date, text: intervalChange.text }}
                      onChange={({ date, text }) => setIntervalChange(c => ({ ...c, date, text, error: null }))}
                      // A correction may concern an old trial
                      startMonth={DONE_DATE_START_MONTH}
                      endMonth={EXPECTED_END_MONTH}
                    />
                    <label className="grid gap-1 text-sm">
                      <span className="font-medium">Reason (required)</span>
                      <textarea
                        value={intervalChange.reason}
                        onChange={(e) => setIntervalChange(c => ({ ...c, reason: e.target.value, error: null }))}
                        maxLength={500}
                        rows={2}
                        className="w-full rounded border px-3 py-2"
                      />
                    </label>
                    <p className="text-sm" data-testid="interval-change-confirm">
                      The Time interval changes from {expectedText(form) || '—'} to {expectedText({ timeinterval: intervalChange.date, timeinterval_text: intervalChange.text }) || '—'}.
                    </p>
                    {intervalChange.error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{intervalChange.error}</p>}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={saveIntervalChange}
                        disabled={!intervalChange.reason.trim() || Object.keys(intervalChangeBody()).length === 1}
                        className="min-h-10 rounded bg-amber-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        Change the time interval
                      </button>
                      <button type="button" onClick={() => setIntervalChange(null)} className="min-h-10 rounded border px-4 text-sm">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="lg:col-span-2" data-testid="diary-planned">
                <span className="block text-sm font-medium mb-1">Planned date</span>
                <PlannedInput
                  value={form.planned_interval}
                  onChange={(v) => setForm(f => ({ ...f, planned_interval: v }))}
                  idPrefix="diary-planned"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Agrolab's planned date - may be outside the Time interval. When it gives a date, the task's status follows it.
                </p>
              </div>

              <div data-testid="diary-done">
                <span className="block text-sm font-medium mb-1">Done date</span>
                <DateField
                  value={form.donedate}
                  onChange={(v) => setForm(f => ({ ...f, donedate: v }))}
                  startMonth={DONE_DATE_START_MONTH}
                  endMonth={DONE_DATE_END_MONTH}
                  label="Done date"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Only once the task has actually been done.
                </p>
              </div>
            </Section>

            <Section title="Completed by · BBCH · data" testId="section-completion">
              {/* Who did it (customer point 61): chosen once there is a done date, by default the signed-in user; the
                  API records who registered it */}
              <div data-testid="diary-completed-by">
                <label htmlFor="diary-completed-by-select" className="block text-sm font-medium mb-1">Completed by</label>
                {form.donedate ? (
                  <CompletedBySelect
                    id="diary-completed-by-select"
                    users={users}
                    value={completedBy}
                    onChange={setCompletedBy}
                    // An old task done before completed_by existed: its persons as written, until someone is chosen
                    placeholder={initialData?.donedate && initialData?.completed_by == null ? completedByName(initialData, users) : undefined}
                  />
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Chosen when the task gets a done date</p>
                )}
                {initialData?.donedate && initialData?.completion_registered_by != null && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="diary-registered-by">
                    Registered by {userName(initialData.completion_registered_by, users)}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="diary-bbch" className="block text-sm font-medium mb-1">BBCH</label>
                <input id="diary-bbch" name="bbch" value={form.bbch} onChange={handleChange} maxLength={DIARY_MAX_LENGTH.bbch} className="min-h-10 w-full rounded border px-3 py-2" />
                <CharCount value={form.bbch} max={DIARY_MAX_LENGTH.bbch} />
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <label className="flex min-h-10 items-center gap-2 text-sm">
                  <input
                    name="dataonpsion"
                    type="checkbox"
                    checked={form.dataonpsion}
                    onChange={e => setForm(f => ({ ...f, dataonpsion: e.target.checked }))}
                    className="h-5 w-5 rounded"
                  />
                  Data collected digitally
                </label>
                <label className="flex min-h-10 items-center gap-2 text-sm">
                  <input
                    name="dataonpaper"
                    type="checkbox"
                    checked={form.dataonpaper}
                    onChange={e => setForm(f => ({ ...f, dataonpaper: e.target.checked }))}
                    className="h-5 w-5 rounded"
                  />
                  Data on paper
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Assigned to <span className="font-normal text-gray-500">(optional)</span></label>
                <SearchSelect
                  // Archived users only when already on the entry
                  options={users
                    .filter(u => !isArchived(u) || selectedPersons.some(person => person.id === u.id))
                    .concat(selectedPersons.filter(person => person.isGhost))}
                  value={selectedPersons}
                  isMulti
                  placeholder="Select persons..."
                  getOptionLabel={person => person.isGhost ? `${person.initials} (unknown user)` : optionLabel(person, person.name || person.initials || person.email)}
                  getOptionValue={person => person.id ?? `ghost-${person.initials}`}
                  onChange={(persons) => { personsTouched.current = true; setSelectedPersons(persons); }}
                  emptyText="No users found"
                />
              </div>
            </Section>

            {/* Attachments section - only show when editing existing diary entry */}
            {initialData?.id && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-emerald-700 dark:text-emerald-400">Photos & Videos</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-3 py-1 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      onClick={handleCameraButtonClick}
                    >
                      Add Photo/Video
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      onClick={handleFileUploaderButtonClick}
                    >
                      Upload Files
                    </button>
                  </div>
                </div>

                {/* Attachments display */}
                {Array.isArray(diaryAttachments) && diaryAttachments.length > 0 ? (
                  <div className="flex flex-wrap gap-4">
                    {diaryAttachments.map(att => {
                      const isImage = att.mime_type && att.mime_type.startsWith('image/');
                      const isVideo = att.mime_type && att.mime_type.startsWith('video/');
                      let url = '';
                      if (att.path) {
                        if (att.path.startsWith('http')) {
                          url = att.path;
                        } else {
                          url = `field/${att.path.replace(/^\/?(attachments\/)?/, '')}`;
                        }
                      }
                      return (
                        <div key={att.id} className="flex flex-col items-center w-32">
                          <div className="focus:outline-none">
                            {isImage && url ? (
                              <SecuredImage
                                src={url}
                                alt={att.original_filename || att.filename}
                                className="w-28 h-20 object-cover rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1"
                              />
                            ) : isVideo && url ? (
                              <video
                                src={url}
                                className="w-28 h-20 object-cover rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1"
                                controls={false}
                                muted
                              />
                            ) : (
                              <div className="w-28 h-20 bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1 flex items-center justify-center">
                                <span className="text-xs text-gray-500">File</span>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400 text-center truncate w-full">
                            {att.original_filename || att.filename}
                          </span>
                          {isImage && <AttachmentDownloadButton attachment={att} className="mt-1" />}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <div className="text-4xl mb-2">📷</div>
                    <div>No photos or videos yet</div>
                    <div className="text-sm">Click "Add Photo/Video" to get started</div>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-6 rounded-b-lg">
          {saveError && (
            <div role="alert" className="mb-3 flex flex-wrap items-center gap-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
              <span className="min-w-0 flex-1">{saveError.message}</span>
              {saveError.retry && (
                <button
                  type="button"
                  onClick={() => handleSubmit({ preventDefault() {} })}
                  className="rounded border border-red-300 px-2 py-1 font-semibold hover:bg-red-100 dark:border-red-700 dark:hover:bg-red-900/40"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {confirmRename && (
            <div role="alertdialog" aria-label="Change the task name" className="mb-3 flex flex-wrap items-center gap-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
              <span className="min-w-0 flex-1">
                Only the name changes. VA No stays {form.vano}, so reports and Excel still show this task as {taskKey(initialData) ?? form.vano}. Change the name?
              </span>
              <button
                type="button"
                onClick={() => {
                  renameConfirmed.current = true;
                  handleSubmit({ preventDefault() {} });
                }}
                className="rounded bg-amber-600 px-3 py-1 font-semibold text-white hover:bg-amber-700"
              >
                Change the name
              </button>
              <button type="button" onClick={() => setConfirmRename(false)} className="rounded border border-amber-300 px-3 py-1 dark:border-amber-700">
                Cancel
              </button>
            </div>
          )}
          {confirmRemoveDone && (
            <div role="alertdialog" aria-label="Remove the done date" className="mb-3 flex flex-wrap items-center gap-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
              <span className="min-w-0 flex-1">Done date will be removed – the task becomes open again.</span>
              <button
                type="button"
                onClick={() => {
                  removeDoneConfirmed.current = true;
                  handleSubmit({ preventDefault() {} });
                }}
                className="rounded bg-amber-600 px-3 py-1 font-semibold text-white hover:bg-amber-700"
              >
                Remove and save
              </button>
              <button type="button" onClick={() => setConfirmRemoveDone(false)} className="rounded border border-amber-300 px-3 py-1 dark:border-amber-700">
                Cancel
              </button>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              className="px-6 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="diary-form"
              onClick={handleSubmit}
              className="px-6 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors"
            >
              Save Entry
            </button>
          </div>
        </div>
      </div >

      {/* Camera Modal */}
      {
        showCameraModal && (
          <Dialog open={showCameraModal} onClose={() => setShowCameraModal(false)} className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
              <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-auto p-6 z-10">
                <div className="flex justify-between items-center mb-4">
                  <div className="font-semibold text-lg text-emerald-700 dark:text-emerald-400">Add Photo/Video</div>
                  <button
                    className="text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl"
                    onClick={() => setShowCameraModal(false)}
                    aria-label="Close"
                  >
                    &times;
                  </button>
                </div>
                <Camera
                  model_type="diary"
                  model_id={initialData?.id}
                  autoActivate={true}
                  onPhotoCapture={handleCaptureSuccess}
                  onVideoCapture={handleCaptureSuccess}
                />
              </div>
            </div>
          </Dialog>
        )
      }

      {/* File Uploader Modal */}
      {
        showFileUploaderModal && (
          <Dialog open={showFileUploaderModal} onClose={() => setShowFileUploaderModal(false)} className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
              <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-auto p-6 z-10">
                <div className="flex justify-between items-center mb-4">
                  <div className="font-semibold text-lg text-blue-700 dark:text-blue-400">Upload Files</div>
                  <button
                    className="text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl"
                    onClick={() => setShowFileUploaderModal(false)}
                    aria-label="Close"
                  >
                    &times;
                  </button>
                </div>
                <FileUploader
                  model_type="diary"
                  model_id={initialData?.id}
                  onFileUpload={handleFileUploadSuccess}
                  acceptedFileTypes="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
                  maxFileSize={50 * 1024 * 1024}
                  multiple={true}
                  title=""
                />
              </div>
            </div>
          </Dialog>
        )
      }
    </div >
  );
}
